import { z } from 'zod';
import { ActiveStatusSchema, RoleSchema } from './enums';
import { PermissionSchema } from './permissions';
import { SchoolCodeSchema, formatSchoolCode, isPlatformEmail } from './validation';

export const MeQuerySchema = z.object({
  /** Código INEP informado no login (ex.: 3512-3456). */
  schoolCode: SchoolCodeSchema.optional(),
  /** Escola ativa já selecionada (sessão existente). */
  schoolId: z.string().trim().min(1).max(128).optional(),
});
export type MeQuery = z.infer<typeof MeQuerySchema>;

export const UserProfileSchema = z.object({
  uid: z.string(),
  email: z.string(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  status: ActiveStatusSchema,
  onboardingCompletedAt: z.string().nullable(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

export const SchoolSummarySchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  inepCode: z.string().nullable(),
});
export type SchoolSummary = z.infer<typeof SchoolSummarySchema>;

export const MembershipSchema = z.object({
  school: SchoolSummarySchema,
  role: RoleSchema,
});
export type Membership = z.infer<typeof MembershipSchema>;

export const MeResponseSchema = z.object({
  profile: UserProfileSchema,
  role: RoleSchema,
  roleLabel: z.string(),
  /** null somente para a equipe TaskAll quando ainda não existe escola cadastrada. */
  school: SchoolSummarySchema.nullable(),
  permissions: z.array(PermissionSchema),
  onboardingCompleted: z.boolean(),
  teacherId: z.string().nullable(),
  /** Equipe TaskAll (acesso às telas de plataforma). */
  platformAdmin: z.boolean(),
  /** Primeiro acesso: precisa trocar a senha provisória antes de usar o sistema. */
  mustChangePassword: z.boolean(),
  memberships: z.array(MembershipSchema),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const CompleteOnboardingSchema = z.object({
  completed: z.literal(true),
});
export type CompleteOnboardingInput = z.infer<typeof CompleteOnboardingSchema>;

/**
 * Login: código INEP da escola (XXXX-XXXX) + credenciais.
 * A equipe TaskAll (@taskall.ai) pode deixar o código em branco — a autorização real é feita na API
 * (e-mail verificado do token), aqui é só conveniência de formulário.
 */
export const LoginFormSchema = z
  .object({
    schoolCode: z.string().trim(),
    email: z.string().trim().pipe(z.email('Informe um e-mail válido')),
    password: z.string().min(6, 'A senha deve ter ao menos 6 caracteres'),
  })
  .superRefine((v, ctx) => {
    if (!v.schoolCode && isPlatformEmail(v.email)) return;
    if (formatSchoolCode(v.schoolCode) === null || !/^[\d\s-]+$/.test(v.schoolCode)) {
      ctx.addIssue({ code: 'custom', path: ['schoolCode'], message: 'Informe o código INEP da escola (8 dígitos: XXXX-XXXX)' });
    }
  })
  .transform((v) => ({ ...v, schoolCode: v.schoolCode ? (formatSchoolCode(v.schoolCode) ?? undefined) : undefined }));
export type LoginFormInput = z.input<typeof LoginFormSchema>;
export type LoginFormValues = z.output<typeof LoginFormSchema>;
